import { System, SystemImpl } from "minecs";
import type { GameModel } from "yage/game/GameModel";
import { Transform } from "yage/schemas/entity/Transform";
import { AirportState, AirportPlane, AirportPilot } from "../components/index.js";
import { missionComplete } from '../domain/progression.js';
import { currentMission } from "../domain/missions.js";
import { AIRPORTS, airlineMode, tier, passivePerMinute, portfolio } from "../domain/airports.js";
import { AirportPassenger } from "../components/index.js";
import { shiftsActive, ON_TIME_FRAMES, BAG_WAIT } from "../domain/act2.js";
import { dispatchStaffed } from "../services/act2.js";
import { contractDeparture } from "../services/contracts.js";
import { gateHeld, stormLive, maintenanceLive } from "../services/events.js";
import { STORM_DELAY, MAINTENANCE_DELAY } from "../domain/events.js";
import { aircraftStand, departurePoint, RUNWAY_Y, STAND_Y, GROUND_FRAMES, groundPose } from "../domain/gateflow.js";
import { ROUTES, PHASE } from "../domain/operations.js";
import { departureTripFrames } from '../domain/flight-duration.js';
import { addMoney, deposit, departurePayout } from "../domain/economy.js";
import { state, career, stations, notice, ids, distance, stop } from "../services/world.js";
import { routes, configureEmptyPlane } from "../services/operations.js";
import { AIRPORT_MINUTE, DEPARTURE_COUNTDOWN, ensureTimetable, recordDeparture, scheduleTurnaround, airportTime, timingStatus } from '../domain/timetable.js';
import { landArrivals } from "./Passengers.js";
import { advanceRetainedAirports, retainedAirports, sweepManagedCash } from "../services/retained.js";
@System(AirportPlane)
export class AirportFlights extends SystemImpl<GameModel> {
    static depth = 4700;
    run = (g: GameModel, id: number) => {
        const e = state(g), p = g.getTypedUnsafe(AirportPlane, id), t = g.getTypedUnsafe(Transform, id);
        if (!e.loaded) return;
        ensureTimetable(p, g.frame);
        const code = tier(e).code, stand = aircraftStand(code, p.gate), standX = stand.x, point = departurePoint(code, p.gate), config = ROUTES[p.routeIndex];
        const port = tier(e);
        if (p.phase === PHASE.BOARDING) {
            t.x = stand.x; t.y = stand.y;
            if (ids(g, AirportPassenger).some(id => { const q = g.getTypedUnsafe(AirportPassenger, id); return q.arriving && q.gate === p.gate && q.walkStep < 2; })) return;
            // A fresh aircraft at an empty gate takes the size of whoever operates it.
            if (p.boarded === 0 && !ids(g, AirportPassenger).some(q => { const x = g.getTypedUnsafe(AirportPassenger, q); return x.state === 3 && x.gate === p.gate; })) {
                configureEmptyPlane(p, e);
            }
            if (p.boardingCalled && p.boarded >= p.seats) {
                if (p.departureReadyFrame < 0) p.departureReadyFrame = g.frame + DEPARTURE_COUNTDOWN;
                p.unattendedSince = -1;
            } else p.departureReadyFrame = -1;
            if (p.visiting) {
                // A completed load needs only a short departure countdown; partial loads keep their closing time.
                // An empty visiting aircraft past its closing time takes the next slot instead of waiting forever with its
                // reservations closed (a deadlock when upstream lines are slow); Act II only, where that flow occurs.
                if (p.boarded === 0 && shiftsActive(e) && g.frame > Math.max(p.scheduledDepartureFrame, p.boardingStartedFrame) + port.boardingWindow + BAG_WAIT) {
                    scheduleTurnaround(p, g.frame); p.boardingCalled = false; p.boardingStartedFrame = -1;
                    return;
                }
                if (!p.boardingCalled || p.boarded === 0) return;
                if (p.boarded >= p.seats && g.frame < p.departureReadyFrame) return;
                // MGT-017: an On-schedule airport closes visiting boarding at the scheduled time instead of holding the window.
                const closing = shiftsActive(e) && e.policyBoarding === 1 ? 0 : port.boardingWindow;
                if (p.boarded < p.seats && g.frame < Math.max(p.scheduledDepartureFrame, p.boardingStartedFrame) + closing) return;
                // Stop new reservations at closing time, then honor every passenger already promised a seat.
                if (ids(g, AirportPassenger).some(id => { const q = g.getTypedUnsafe(AirportPassenger, id); return q.state === 3 && q.gate === p.gate; })) return;
                // MGT-020: a gate crisis holds this gate's departure until it is fixed or resolves.
                if (shiftsActive(e) && gateHeld(e, p.gate)) return;
                // MGT-007: from Act II an aircraft waits for its passengers' bags, up to BAG_WAIT frames past readiness, then leaves late.
                const bagsNeeded = shiftsActive(e) && stations(g)[1]?.unlocked ? p.boarded : 0;
                if (e.holdBags < bagsNeeded && g.frame < Math.max(p.scheduledDepartureFrame, p.boardingStartedFrame) + port.boardingWindow + BAG_WAIT) {
                    if (g.frame % 600 === 0) notice(g, `${port.aircraft} at Gate ${String.fromCharCode(65 + p.gate)} is waiting for ${bagsNeeded - e.holdBags} bags. Carry bags to the loading belt.`);
                    return;
                }
                e.holdBags = Math.max(0, e.holdBags - bagsNeeded);
                p.phase = PHASE.TAXI; p.clock = 0; p.pilot = -1;
                // A staffed Flight Dispatch turns visiting aircraft around 20% faster (MGT-011).
                p.tripFrames = departureTripFrames(Math.round(port.turnaround * (dispatchStaffed(g) ? 0.8 : 1)), g.frame - p.scheduledDepartureFrame, true) + Math.round(((shiftsActive(e) && stormLive(e) ? STORM_DELAY : 0) + (shiftsActive(e) && maintenanceLive(e) ? MAINTENANCE_DELAY : 0)) * (dispatchStaffed(g) ? 0.5 : 1));
                recordDeparture(p, g.frame, e.run > 1 && g.rand.int(0, 9) < 2 ? (g.rand.int(0, 1) ? 3 : -2) * AIRPORT_MINUTE : 0);
                e.flights++;
                e.handsOffFlights = p.boarded * 10 >= p.seats * 9 ? e.handsOffFlights + 1 : 0;
                // On time = left within the normal boarding window plus two airport minutes (bags, crises and storms make it late).
                const onTime = g.frame - p.scheduledDepartureFrame <= (e.policyBoarding === 1 ? 0 : port.boardingWindow) + ON_TIME_FRAMES;
                if (shiftsActive(e)) { e.shiftDepartures++; if (onTime) e.shiftOnTime++; }
                e.shiftEarned += contractDeparture(g, p.gate, onTime);
                // Act I: the departure itself pays, so the flight the lessons chase is also the money moment.
                const paid = shiftsActive(e) ? 0 : deposit(e, stations(g)[6], departurePayout(p.boarded, p.seats, port.fare));
                notice(g, `${port.aircraft} departed Gate ${String.fromCharCode(65 + p.gate)} with ${p.boarded}/${p.seats} aboard${paid ? ` · +${paid} at the gate` : ''}.`);
                return;
            }
            if (p.boarded < p.seats) return; // Your own airline never departs with an incomplete load.
            p.boarded = p.seats; p.phase = PHASE.READY; p.clock = 0;
            notice(g, `Gate ${String.fromCharCode(65 + p.gate)} is FULL (${p.seats}/${p.seats}). An assigned pilot handles departure.`);
        }
        if (p.phase === PHASE.READY) {
            if (p.departureReadyFrame < 0) p.departureReadyFrame = g.frame + DEPARTURE_COUNTDOWN;
            if (g.frame < Math.max(p.departureReadyFrame, p.nextDepartureFrame)) return;
            const pilot = ids(g, AirportPilot).find(id => { const a = g.getTypedUnsafe(AirportPilot, id); return a.assignedGate === p.gate && a.flyingGate < 0 && distance(g.getTypedUnsafe(Transform, id), point) < 42; });
            if (pilot === undefined) return;
            p.pilot = pilot;
            g.getTypedUnsafe(AirportPilot, pilot).flyingGate = p.gate;
            stop(g, pilot);
            p.phase = PHASE.TAXI; p.clock = 0;
            p.tripFrames = departureTripFrames(config.flightFrames, g.frame - p.scheduledDepartureFrame, false);
            p.nextDepartureFrame = g.frame + p.scheduleFrames; recordDeparture(p, g.frame, e.run > 1 && g.rand.int(0, 9) < 2 ? (g.rand.int(0, 1) ? 3 : -2) * AIRPORT_MINUTE : 0); e.flights++; e.handsOffFlights++;
            if (shiftsActive(e)) { e.shiftDepartures++; if (g.frame - p.scheduledDepartureFrame <= ON_TIME_FRAMES) e.shiftOnTime++; }
            notice(g, `${config.name}: all ${p.seats} passengers aboard. Your assigned pilot is flying.`);
            return;
        }
        p.clock++;
        if (p.phase === PHASE.TAXI) {
            // Push back and taxi to the runway, then accelerate nose-first along it.
            const at = groundPose(code, p.gate, p.clock, false);
            t.x = at.x; t.y = at.y;
            if (p.clock >= GROUND_FRAMES) { p.phase = PHASE.AIRBORNE; p.clock = 0; }
        } else if (p.phase === PHASE.AIRBORNE) {
            // Cruise position is presentation data in the native world, not a second animation loop.
            t.x = standX; t.y = 110;
            p.clock = Math.min(p.clock, p.tripFrames);
            if (g.frame >= p.expectedArrivalFrame - GROUND_FRAMES) { p.phase = PHASE.APPROACH; p.clock = 0; }
        } else if (p.phase === PHASE.APPROACH) {
            // Land on the runway, slow down, then taxi back into the stand before opening the gate.
            const at = groundPose(code, p.gate, p.clock, true);
            t.x = at.x; t.y = at.y;
            if (p.clock < GROUND_FRAMES) return;
            p.arrivalOffset = g.frame - p.scheduledArrivalFrame;
            landArrivals(g, p);
            if (p.visiting) {
                // The next visiting aircraft takes over after this one returns.
                Object.assign(p, { phase: PHASE.BOARDING, boardingCalled: false, clock: 0, boarded: 0, flightNumber: p.flightNumber + 1 });
                configureEmptyPlane(p, e);
                notice(g, `Inbound SH${p.flightNumber - 1} returned to Gate ${String.fromCharCode(65+p.gate)} at ${airportTime(g.frame)} · ${timingStatus(p.arrivalOffset)}. Preparing SH${p.flightNumber}.`);
                scheduleTurnaround(p, g.frame);
                if (e.run > 1 && (p.flightNumber + p.gate) % 5 === 0) p.aircraftReadyFrame = p.scheduledDepartureFrame + 3 * AIRPORT_MINUTE;
                t.x = stand.x; t.y = stand.y;
                return;
            }
            routes(g)[p.routeIndex].flights++;
            if (config.international) e.internationalFlights++;
            if (p.pilot >= 0 && g.hasComponent(AirportPilot, p.pilot)) { const a = g.getTypedUnsafe(AirportPilot, p.pilot); a.flyingGate = -1; a.flights++; }
            Object.assign(p, { phase: PHASE.BOARDING, boardingCalled: false, clock: 0, boarded: 0, pilot: -1, flightNumber: p.flightNumber + 1 });
            configureEmptyPlane(p, e);
            scheduleTurnaround(p, g.frame);
            if (e.run > 1 && (p.flightNumber + p.gate) % 5 === 0) p.aircraftReadyFrame = p.scheduledDepartureFrame + 3 * AIRPORT_MINUTE;
            t.x = stand.x; t.y = stand.y;
            notice(g, `${config.name} returned. The aircraft is ready for its next passengers.`);
        }
    };
}
@System(AirportState)
export class AirportObjectives extends SystemImpl<GameModel> {
    static depth = 4800;
    run = (g: GameModel) => {
        const e = state(g);
        if (!e.loaded)
            return;
        const mission = currentMission(e);
        if (!mission)
            return;
        const ss = stations(g);
        // A completed-in-advance lesson still gets a moment on screen before advancing.
        if (g.frame - (e.missionSince ?? 0) >= 180 && missionComplete(e, ss)) {
            e.mission++;
            e.missionSince = g.frame;
            if (mission.reward)
                addMoney(career(g), e, mission.reward);
            notice(g, `${mission.title} complete${mission.reward ? ` · +${mission.reward} bills` : ''}`, 3);
        }
    };
}

/** Managed airports elsewhere in the career run on L1 while players are here, in 30-second batches (split-invariant). */
@System(AirportState)
export class AirportManagedNetwork extends SystemImpl<GameModel> {
    static depth = 4860;
    run = (g: GameModel) => {
        if (!state(g).loaded || g.frame % 1800 !== 0) return;
        advanceRetainedAirports(g, 30);
        // Managed airports' floor cash arrives at this Check-In, as the flat portfolio income did.
        const swept = sweepManagedCash(g);
        if (swept.total > 0 && deposit(state(g), stations(g)[0], swept.total) > 0)
            notice(g, `${swept.airports.map(code => AIRPORTS.find(a => a.code === code)?.name ?? code).join(' + ')} sent ${swept.total.toLocaleString('en-US')} bills to Check-In.`);
    };
}

/** Airports you have moved on from keep running and send their takings every 30 seconds. */
@System(AirportState)
export class AirportPortfolio extends SystemImpl<GameModel> {
    static depth = 4850;
    run = (g: GameModel) => {
        const e = state(g);
        if (!e.loaded || g.frame % 1800 !== 0) return;
        const managed = retainedAirports(g).filter(r => r.managed).map(r => r.airportId), flat = portfolio(e).filter(a => !managed.includes(a.code));
        if (!flat.length) return;
        const amount = Math.floor(passivePerMinute(e, career(g).prestige, managed) / 2);
        if (deposit(e, stations(g)[0], amount) > 0)
            notice(g, `${flat.map(a => a.name).join(' + ')} sent ${amount.toLocaleString('en-US')} bills to Check-In.`);
    };
}
